import { EThemeName, IThemeProperty, IThemesConfig } from '@wind-log_library/fe-component-library';
import { environment } from 'environments/environment';

const FILE_SERVER_BASE_URL = environment.shared_server + '/fe-component-library/';

const light: IThemeProperty = {
  name: EThemeName.LIGHT,
  properties: {
    '--primary': '#1A1A1A',
    '--primary-background': '#009FDA',
    '--secondary': '#88c0e3',
    '--tertiary': '#85a3b6',
    '--target': '#B69785',
    '--background': '#F6F6F9',
    '--font': '#636669',
    '--white': '#ffffff',
    '--black': '#000000',
    '--gray100': '#c2d1da',
    '--red': '#dc3545',
    '--yellow': '#ffc107',
    '--green': '#28a745',
    '--cyan': '#17a2b8',
    '--card': '#ffffff',
    '--divider': '#dfdfdf',
    '--limited-operation': '#ffe6ff',
    '--alarm-stop': '#fbe5e8',
    '--in-service': '#dce9f7',
    '--graph': '#f2f5f7',
    '--bg-image': '#F4FBFE',
    '--table-last-row': '#d5d6df',
    '--table-odd-row': '#f2f5f8',
    '--orange': '#e28545',
    '--primary-opposite': '#ffffff',
    '--line-charts-background': '#e5f5fb',
    '--theoretical-line-charts-background': '#f6b2b7',
    '--prod-loss-charts-background': '#f6b2b7',
    '--charts-background': '#c2d1da',
    '--charts-primary': '#99D9F0',
    '--theme-switch': '#c2d1da',
    '--theme-switch-handle': '#009FDA',
    '--theme-switch-icon': 'url("' + FILE_SERVER_BASE_URL + 'general-icons/theme/Theme_Light_Mode.svg")',
    '--black-line-chart': '#1A1A1A',

    '--gray-100': '#f8f9fa',
    '--gray-200': '#F2F2F4',
    '--gray-300': '#f5f7f9',
    '--gray-400': '#E6E6EA',
    '--gray-600': '#868e96',
    '--event-category-grid-lim': '#7d3f7c',
    '--event-category-environmental': '#187175',
    '--event-category-repairs': '#56ccff',
    '--event-category-maintainance': '#4a90e2',
    '--event-category-inspection': '#F46B39',
    '--error-background': '#fbe3e7',
    '--kpi-total': '#e5eaf0',
    '--ts-button-bg': '#EEF1F5',
    '--ts-content-bg': '#F5F8F9',
    '--kpi-summary-table-odd': '#f2f5f7',
    '--kpi-summary-table-even': '#e7eff3',
    '--kpi-below': '#368DE0',
    '--kpi-normal': '#00C875',
    '--kpi-above': '#DC3545',
    '--kpi-wind': '#1bbb9b',
    '--kpi-delta': '#D1D4DE',

    '--budget-follow-up-delivered-scada': '#00DFD7',
    '--budget-follow-up-delivered-meter': '#00C1E2',
    '--budget-follow-up-delivered-revenues': '#85DEFF',
    '--budget-follow-up-expected-production': '#95C5F2',
    '--budget-follow-up-compensated-logbook': '#A378A3',
    '--purple': '#7c3f7c',
    '--budget-follow-up-theoretical': '#92A1C4',
    '--budget-follow-up-budget': '#B48245',
    '--budget-follow-up-budget-ytd': '#6b4c26',
    '--budget-follow-up-deviation-green': '#79C041',
    '--budget-follow-up-deviation-red': '#DD2200',

    '--logbook-status': '#17A2B8',
    '--logbook-warning': '#F78D5B',
    '--logbook-error': '#E2445C',

    // Forecast chart colors, but reusable in other charts:

    '--chart-bg-color-theo': '#F5B2B7',
    '--chart-border-color-theo': '#DF000F',
    '--chart-bg-color-theo-amended': '#E6E6F0',
    '--chart-border-color-theo-amended': '#BEC8D7',
    '--chart-bg-color-blue': '#58D3F7',
    '--chart-bg-color-green': 'green',
    '--chart-bg-color-orange': '#EC9400',
    '--chart-bg-color-brown': '#a36e77',
    '--chart-bg-color-purple': '#7c3f7c',
    '--chart-bg-color-gray': '#868e96',
    '--chart-bg-default-color': '#e5e5e5',

    '--chart-availability-percentage-1': '#DF000F',
    '--chart-availability-percentage-2': '#F59298',
    '--chart-availability-percentage-3': '#FFCB00',
    '--chart-availability-percentage-4': '#FFE992',
    '--chart-availability-percentage-5': '#A7D687',
    '--chart-availability-percentage-6': '#00D700',

    '--chart-availability-status-1': '#00D700',
    '--chart-availability-status-2': '#FFCB00',
    '--chart-availability-status-3': '#DF000F',
    '--dropdown-background': '#F8F9FA',
    '--accent-button': '#009FDA',
    '--chart-availability': '#f0cf65',
    '--scroll-background': '#F6F6F9',

    // Colors for forecast providers
    '--forecast-provider-horizon': '#007FAE',
    '--forecast-provider-meteo-blue': '#7c3f7c',
    '--forecast-provider-open-weather': '#26AEA3',
    '--forecast-provider-meteologica': '#BD2E56',
    '--forecast-provider-dnv-forecaster': '#003591',
    '--forecast-provider-bkw-trading': '#999999',

    // Colors for nested expandible/collapsable tables (like predictive module ones)

    '--nested-table-main-row': '#E6E6EA',
    '--nested-table-inner-row': '#F2F2F4',
    '--textarea-background': '#ffffff',

    '--warning-message-background': '#FFFDE1',
    '--warning-message-border': '#FFC107',

    '--navigation-border': '#eceef1',
  },
};

const dark: IThemeProperty = {
  name: EThemeName.DARK,
  properties: {
    '--primary': '#ffffff',
    '--primary-background': '#009FDA',
    '--secondary': '#88c0e3',
    '--tertiary': '#85a3b6',
    '--target': '#B69785',
    '--background': '#00040a',
    '--font': '#ADB5BD',
    '--white': '#ffffff',
    '--black': '#000000',
    '--gray100': '#c2d1da',
    '--red': '#dc3545',
    '--yellow': '#ffc107',
    '--green': '#28a745',
    '--cyan': '#17a2b8',
    '--card': '#131b25',
    '--divider': '#374c5d',
    '--limited-operation': '#5f3c5f',
    '--alarm-stop': '#54383c',
    '--in-service': '#324156',
    '--graph': '#2e5067',
    '--bg-image': '#0f303e',
    '--table-last-row': '#454750',
    '--table-odd-row': '#1f2831',
    '--orange': '#e28545',
    '--primary-opposite': '#1A1A1A',
    '--line-charts-background': '#162837',
    '--theoretical-line-charts-background': '#f6b2b7',
    '--prod-loss-charts-background': '#C27076',
    '--charts-background': '#040507',
    '--charts-primary': '#99D9F0',
    '--theme-switch': '#4c5f6e',
    '--theme-switch-handle': '#009FDA',
    '--theme-switch-icon': 'url("' + FILE_SERVER_BASE_URL + 'general-icons/theme/Theme_Dark_Mode.svg")',
    '--black-line-chart': '#ECEEF1',

    '--gray-100': '#f8f9fa',
    '--gray-200': '#F2F2F4',
    '--gray-600': '#868e96',
    '--gray-300': '#f5f7f9',
    '--gray-400': '#E6E6EA',
    '--event-category-grid-lim': '#7d3f7c',
    '--event-category-environmental': '#187175',
    '--event-category-repairs': '#56ccff',
    '--event-category-maintainance': '#4a90e2',
    '--event-category-inspection': '#F46B39',
    '--error-background': '#fbe3e7',
    '--kpi-total': '#28476d',
    '--ts-button-bg': '#314b6d',
    '--ts-content-bg': '#013648',
    '--kpi-summary-table-odd': '#1A222B',
    '--kpi-summary-table-even': '#252C36',
    '--kpi-below': '#368DE0',
    '--kpi-normal': '#00C875',
    '--kpi-above': '#DC3545',
    '--kpi-wind': '#1bbb9b',
    '--kpi-delta': '#D1D4DE',

    '--budget-follow-up-delivered-scada': '#00DFD7',
    '--budget-follow-up-delivered-meter': '#00C1E2',
    '--budget-follow-up-delivered-revenues': '#85DEFF',
    '--budget-follow-up-expected-production': '#95C5F2',
    '--budget-follow-up-compensated-logbook': '#A378A3',
    '--budget-follow-up-theoretical': '#92A1C4',
    '--purple': '#7c3f7c',
    '--budget-follow-up-budget': '#B48245',
    '--budget-follow-up-budget-ytd': '#6b4c26',
    '--budget-follow-up-deviation-green': '#79C041',
    '--budget-follow-up-deviation-red': '#DD2200',

    '--logbook-status': '#17A2B8',
    '--logbook-warning': '#F78D5B',
    '--logbook-error': '#E2445C',

    // Forecast chart colors, but reusable in other charts:
    '--chart-bg-color-theo': '#F5B2B7',
    '--chart-border-color-theo': '#DF000F',
    '--chart-bg-color-theo-amended': '#242E3A',
    '--chart-border-color-theo-amended': '#85A3B6',
    '--chart-bg-color-blue': '#58D3F7',
    '--chart-bg-color-green': 'green',
    '--chart-bg-color-orange': '#EC9400',
    '--chart-bg-color-brown': '#a36e77',
    '--chart-bg-color-purple': '#7c3f7c',
    '--chart-bg-color-gray': '#868e96',
    '--chart-bg-default-color': '#495057',

    '--chart-availability-percentage-1': '#DF000F',
    '--chart-availability-percentage-2': '#F59298',
    '--chart-availability-percentage-3': '#FFCB00',
    '--chart-availability-percentage-4': '#FFE992',
    '--chart-availability-percentage-5': '#A7D687',
    '--chart-availability-percentage-6': '#00D700',

    '--chart-availability-status-1': '#00D700',
    '--chart-availability-status-2': '#FFCB00',
    '--chart-availability-status-3': '#DF000F',
    '--dropdown-background': '#343A40',
    '--accent-button': '#009FDA',
    '--chart-availability': '#f0cf65',
    '--scroll-background': '#353E46',

    // Colors for forecast providers
    '--forecast-provider-horizon': '#56AECE',
    '--forecast-provider-meteo-blue': '#946294',
    '--forecast-provider-open-weather': '#51BEB5',
    '--forecast-provider-meteologica': '#CA5878',
    '--forecast-provider-dnv-forecaster': '#4a74BD',
    '--forecast-provider-bkw-trading': '#999999',

    '--nested-table-main-row': '#4C535A',
    '--nested-table-inner-row': '#353E46',

    '--textarea-background': '#495057',

    '--navigation-border': '#2b323b',
  },
};

export const themeConfig: IThemesConfig = {
  themeStoreName: 'wing-log-theme',
  config: [light, dark],
};
